import { AgentGlyph } from "@/product-stage/AgentGlyph.tsx";
import { cn } from "@/lib/cn.ts";
import type { ReplicaAgent } from "@/product-stage/demo/types.ts";

export function AgentMark({
  agent,
  variant = "sidebar",
  animated = false
}: {
  agent: ReplicaAgent;
  variant?: "sidebar" | "hud" | "message" | "context";
  animated?: boolean;
}) {
  const working = agent.status === "working";
  const icon = (
    <AgentGlyph
      seed={agent.seed}
      expression={working ? "thinking" : "neutral"}
      animated={animated || working}
      label={agent.name}
    />
  );

  if (variant === "sidebar") {
    return <span className="conversation-avatar conversation-agent-avatar">{icon}</span>;
  }
  if (variant === "context") {
    return <span className="agent-thread-context-mark">{icon}</span>;
  }
  if (variant === "message") {
    return <span className="message-avatar message-agent-avatar">{icon}</span>;
  }
  const status = working ? "working" : agent.status === "present" ? "online" : "idle";
  return (
    <span className="agent-avatar">
      {icon}
      <b data-status={status} />
    </span>
  );
}

const IDENTICON_TONES = [
  { background: "#e8f6ee", color: "#1f8a4c" },
  { background: "#eef0ff", color: "#5b51bf" },
  { background: "#e7f4fb", color: "#2178b0" },
  { background: "#fff1e6", color: "#c56a16" },
  { background: "#fdecef", color: "#c4455e" },
  { background: "#e7f7f3", color: "#1a8a78" }
] as const;

export function identiconTone(seed: string): (typeof IDENTICON_TONES)[number] {
  return IDENTICON_TONES[identiconHash(seed) % IDENTICON_TONES.length]!;
}

export function Identicon({
  seed,
  className
}: {
  seed: string;
  className?: string;
}) {
  const cells = identiconCells(seed);
  const tone = identiconTone(seed);
  return (
    <span
      className={cn("identicon", className)}
      aria-hidden
      style={{ background: tone.background, color: tone.color }}
    >
      <svg viewBox="0 0 5 5">
        {cells.map((on, index) =>
          on ? <rect key={index} x={index % 5} y={Math.floor(index / 5)} width="1" height="1" /> : null
        )}
      </svg>
    </span>
  );
}

function identiconHash(seed: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function identiconCells(seed: string): boolean[] {
  let hash = identiconHash(seed);
  const cells = Array.from({ length: 25 }, () => false);
  for (let y = 0; y < 5; y += 1) {
    for (let x = 0; x < 3; x += 1) {
      hash = Math.imul(hash ^ (y * 5 + x + 1), 0x01000193);
      const on = (hash >>> 0) % 3 !== 0;
      cells[y * 5 + x] = on;
      cells[y * 5 + (4 - x)] = on;
    }
  }
  return cells;
}
